import React from 'react';
import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
} from '@mui/material';
import type { ProcessedStoreRow } from '../../../utils/storeSummaryTransform';
import type { StoreSummarySortKey, StoreSummaryTableColumn } from '../../../types/features/storeSummaryListing';

interface StoreSummaryStoresTableProps {
  columns: StoreSummaryTableColumn[];
  rows: ProcessedStoreRow[];
  orderBy: StoreSummarySortKey | null;
  order: 'asc' | 'desc';
  onRequestSort: (key: StoreSummarySortKey) => void;
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onRowsPerPageChange: (pageSize: number) => void;
  onRowClick: (storeId: string) => void;
  loading?: boolean;
}

const StoreSummaryStoresTable: React.FC<StoreSummaryStoresTableProps> = ({
  columns,
  rows,
  orderBy,
  order,
  onRequestSort,
  total,
  page,
  pageSize,
  onPageChange,
  onRowsPerPageChange,
  onRowClick,
  loading = false,
}) => {
  const columnCount = columns.length;

  return (
    <>
      <TableContainer sx={loading && rows.length === 0 ? { height: 'auto', minHeight: 120, maxHeight: 200 } : undefined}>
        <Table size="small">
          <TableHead>
            <TableRow sx={{ background: '#F3F2F7', color: '#222B45' }}>
              {columns.map((column) => (
                <TableCell
                  key={column.id}
                  sx={{
                    color: '#222B45',
                    fontSize: '13px',
                    fontWeight: 600,
                    lineHeight: 1.15,
                    minWidth: column.width,
                    p: '4px 12px',
                    textAlign: column.align || 'left',
                    width: column.width,
                  }}
                >
                  {column.sortable && column.getSortKey ? (
                    <TableSortLabel
                      active={orderBy === column.getSortKey()}
                      direction={orderBy === column.getSortKey() ? order : 'asc'}
                      hideSortIcon={false}
                      onClick={() => onRequestSort(column.getSortKey!())}
                      sx={{
                        '& .MuiTableSortLabel-icon': {
                          color: orderBy === column.getSortKey() ? '#222B45 !important' : '#9AA3AF !important',
                          opacity: 1,
                        },
                        color: '#222B45',
                        justifyContent: column.align === 'right' ? 'flex-end' : column.align === 'center' ? 'center' : 'flex-start',
                        width: '100%',
                      }}
                    >
                      <Box sx={{ textAlign: column.align || 'left' }}>
                        {column.labelLines.map((line, index) => (
                          <span key={index}>
                            {line}
                            {column.labelLines.length > 1 ? <br /> : null}
                          </span>
                        ))}
                      </Box>
                    </TableSortLabel>
                  ) : (
                    column.labelLines.map((line, index) => (
                      <span key={index}>
                        {line}
                        {column.labelLines.length > 1 ? <br /> : null}
                      </span>
                    ))
                  )}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {loading && rows.length === 0 ? (
              <TableRow sx={{ height: 72 }}>
                <TableCell colSpan={columnCount} sx={{ height: 72, padding: 0, border: 0, verticalAlign: 'middle', textAlign: 'center' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 72 }}>
                    <CircularProgress size={28} />
                  </Box>
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell colSpan={columnCount} sx={{ py: 4, textAlign: 'center', border: 0 }}>
                  <Typography variant="body1" color="text.secondary">
                    No stores found
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, index) => (
                <TableRow
                  key={row.id}
                  sx={{
                    background: index % 2 === 0 ? '#FFFFFF' : '#F8F9FB',
                    cursor: 'pointer',
                    height: 40,
                    opacity: loading && rows.length > 0 ? 0.6 : 1,
                    pointerEvents: loading && rows.length > 0 ? 'none' : 'auto',
                  }}
                >
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      sx={{
                        borderBottomColor: '#ECEEF3',
                        lineHeight: 1.2,
                        minWidth: column.width,
                        p: '4px 12px',
                        textAlign: column.align || 'left',
                        width: column.width,
                      }}
                      onClick={() => onRowClick(row.id)}
                    >
                      {column.renderCell(row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        rowsPerPageOptions={[25, 50, 75, 100]}
        count={total}
        rowsPerPage={pageSize}
        page={Math.max(0, page - 1)}
        onPageChange={(_event, nextPage) => onPageChange(nextPage + 1)}
        onRowsPerPageChange={(event) => onRowsPerPageChange(parseInt(event.target.value, 10))}
        sx={{
          '.MuiTablePagination-toolbar': { minHeight: 40 },
          '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: 12 },
        }}
      />
    </>
  );
};

export default React.memo(StoreSummaryStoresTable);
